import { FeatureUsage } from '../../../../../shared/types/feature-usage';
import { isProspectMaxActive } from '../../../../../shared/utils';
import { getEmailServiceProviderGroups } from '../../../../../shared/utils/get-email-accounts-esp-mapping';
import { generateMultiLevelSelectOptions } from '../../../../../shared/utils/get-verification-sub-status-mapping';
import { isAgencyUser } from '../../../../../shared/utils/user-details';
import {
  ContactStatusType,
  ContactStatusTypeDisplayName,
  ContactVerificationStatus,
  ContactVerificationStatusTypeDisplayName,
  CreationSourceDisplayName,
  ProspectCreationSource,
} from '../../../../sequence/enums';

import {
  ProspectFilterField,
  FilterSource,
  ProspectFiltersFieldsAttributesValue,
} from '../types';
// eslint-disable-next-line import/no-cycle
import { prospectFiltersFieldsKeysFilterOptions } from './helpers';

export const staticDropdownKeysForProspectFilter = [
  'verificationStatus',
  'verificationSubStatus',
  'freeEmailAccount',
  'creationSource',
  'status',
  'emailServiceProvider',
];

const getStaticDropdownForProspectFilter = (
  key,
  featureUsage: FeatureUsage[],
  isProspectEnrollmentEnabled?: boolean,
) => {
  if (key === 'verificationStatus') {
    return [
      {
        key: ContactVerificationStatus.Valid,
        name: ContactVerificationStatusTypeDisplayName.Valid,
      },
      {
        key: ContactVerificationStatus.Risky,
        name: ContactVerificationStatusTypeDisplayName.Risky,
      },
      {
        key: ContactVerificationStatus.Bad,
        name: ContactVerificationStatusTypeDisplayName.Bad,
      },
      {
        key: ContactVerificationStatus.Unverified,
        name: ContactVerificationStatusTypeDisplayName.Unverified,
      },
    ];
  }
  if (key === 'verificationSubStatus') {
    return generateMultiLevelSelectOptions();
  }

  if (key === 'creationSource') {
    return [
      {
        key: ProspectCreationSource.manually_created,
        name: CreationSourceDisplayName.Manually_Created,
      },
      {
        key: ProspectCreationSource.csv_import,
        name: CreationSourceDisplayName.CSV_Import,
      },
      {
        key: ProspectCreationSource.lead_finder,
        name: CreationSourceDisplayName.Lead_Finder,
      },
      {
        key: ProspectCreationSource.api,
        name: CreationSourceDisplayName.API,
      },
      {
        key: ProspectCreationSource.chrome_extension,
        name: CreationSourceDisplayName.Chrome_Extention,
      },
      {
        key: ProspectCreationSource.unified_inbox,
        name: CreationSourceDisplayName.Unified_Inbox,
      },
    ];
  }

  if (key === 'status') {
    const isProspectActiveMax = !!isProspectMaxActive(featureUsage);

    const status = [
      {
        key: ContactStatusType.Active,
        name: ContactStatusTypeDisplayName.active,
      },
      {
        key: ContactStatusType.Inactive,
        name: ContactStatusTypeDisplayName.inActive,
      },
      {
        key: ContactStatusType.Paused,
        name: ContactStatusTypeDisplayName.paused,
      },
      {
        key: ContactStatusType.Replied,
        name: ContactStatusTypeDisplayName.replied,
      },
      {
        key: ContactStatusType.Completed,
        name: ContactStatusTypeDisplayName.completed,
      },
      {
        key: ContactStatusType.Waiting,
        name: ContactStatusTypeDisplayName.waiting,
      },
      ...(isProspectEnrollmentEnabled
        ? [
            {
              key: ContactStatusType.Upcoming,
              name: ContactStatusTypeDisplayName.upcoming,
            },
          ]
        : []),
      {
        key: ContactStatusType.Finished,
        name: ContactStatusTypeDisplayName.finished,
      },
      {
        key: ContactStatusType.Bounced,
        name: ContactStatusTypeDisplayName.bounced,
      },
      {
        key: ContactStatusType.Unsubscribed,
        name: ContactStatusTypeDisplayName.unsubscribed,
      },
    ];

    return isProspectActiveMax
      ? status
      : status?.filter(
          ({ name }) => name !== ContactStatusTypeDisplayName.waiting,
        );
  }

  if (key === 'emailServiceProvider') {
    return getEmailServiceProviderGroups();
  }

  return [];
};

export const getProspectFilterFieldAttributeValues = (
  field: ProspectFilterField,
  value: (string | number)[],
  selectedValues: any[],
  prospectFiltersFieldsAttributesValue: ProspectFiltersFieldsAttributesValue,
  featureUsages: FeatureUsage[],
  isProspectEnrollmentEnabled?: boolean,
) => {
  if (staticDropdownKeysForProspectFilter.includes(field?.key)) {
    return {
      key: field?.key,
      meta: null,
      options: getStaticDropdownForProspectFilter(
        field?.key,
        featureUsages,
        isProspectEnrollmentEnabled,
      ),
    };
  }

  let keyValue = field?.key;
  let fieldValue = prospectFiltersFieldsAttributesValue?.[field?.key];

  if (field?.source === FilterSource.FIELD) {
    keyValue = field?.id;
    fieldValue = prospectFiltersFieldsAttributesValue?.[field?.id];
  }

  const meta = fieldValue?.meta || null;
  const options =
    fieldValue?.payload?.map((item) => ({
      key: item?.value,
      ...item,
    })) || [];

  const shouldFetch = options?.length === 0;
  const sendValuesInPayload = [
    'sequence',
    'clientSequenceAssociation',
    'fileName',
    'subSequence',
  ].includes(keyValue);

  let doNotUpdateOptions = prospectFiltersFieldsKeysFilterOptions?.includes(
    keyValue,
  );

  if (keyValue === 'owners' && isAgencyUser()) {
    doNotUpdateOptions = false;
  }

  const isLeadFinderListField = keyValue === 'leadFinderList';

  const updatedOptions =
    doNotUpdateOptions || isLeadFinderListField
      ? options
      : [
          ...(sendValuesInPayload
            ? selectedValues?.filter(
                (item) =>
                  !options?.some(
                    (option) =>
                      option.value?.toString() === item.value?.toString(),
                  ),
              )
            : value
                ?.filter(
                  (val) =>
                    !options?.some(
                      (option) => option.value?.toString() === val?.toString(),
                    ),
                )
                ?.map((val) => ({
                  key: val,
                  name: val,
                  value: val,
                }))),
          ...options,
        ];

  return {
    key: keyValue,
    shouldFetch,
    sendValuesInPayload,
    meta,
    options: updatedOptions,
  };
};
